import { VersionRange } from '../../models/core/version-range.js';
import {
  cloneListOfCloneable, DataModelBuilder, isSameData, isSameList,
  SerializationContext, SerializedData,
} from '../../models/dto/dto.js';
import { BusinessModuleRef } from '../../models/serializable/business-module-ref.js';
import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

/**
 * @deprecated
 */
export class LegacyConsentComponent extends Component {
  static readonly TYPE_ID: string = '16bc0e2a0f6d4cc28a0d4d7a4dc39e05';

  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly marketing: boolean,
    public readonly businessMarketing: BusinessModuleRef[],
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyConsentComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacyConsentComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static create(args: {
    marketing: boolean,
    businessMarketing: BusinessModuleRef[],
  }): LegacyConsentComponent {
    return new LegacyConsentComponent(
        args.marketing,
        args.businessMarketing,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyConsentComponent {
    return LegacyConsentComponent.create({
      marketing: MapUtils.getBoolean(map, 'marketing'),
      businessMarketing: MapUtils.getList<BusinessModuleRef>(
          map,
          'businessMarketing',
          (data) => BusinessModuleRef.mustDecode(JSONUtils.decodeString(data))
      ),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'marketing': this.marketing,
      'businessMarketing': this.businessMarketing.map((item) => item.encode()),
    };
  }

  clone(): this {
    return LegacyConsentComponent.create({
      marketing: this.marketing,
      businessMarketing: cloneListOfCloneable(this.businessMarketing),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.marketing === this.marketing &&
      isSameList(other.businessMarketing, this.businessMarketing, isSameData);
  }
}

export class LegacyConsentComponentBuilder extends DataModelBuilder<LegacyConsentComponent> {
  build(context: SerializationContext, map: SerializedData): LegacyConsentComponent {
    return LegacyConsentComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacyConsentComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyConsentComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - add `businessMarketing` field of type {@link BusinessModuleRef[]}.
     */
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'businessMarketing', []);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
